import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { orderBy } from 'lodash';
import toast from 'react-hot-toast';
import { THEME_MESSAGES } from 'utils/constants';
import { getAllStoresForBusiness } from 'api/admin/businesses/businesses';
import { ApiError } from 'types/api';

interface GetStoresForBusinessOutput {
  success: boolean;
  stores: Array<{
    id: number;
    name: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    phoneNumber?: string;
    storeTheme?: any;
  }>;
}

export interface Store {
  id: number;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  phoneNumber?: string;
  theme?: any;
}

export const useStoresForBusiness = (businessId: string | number, enabled: boolean = true) => {
  return useQuery<unknown, ApiError, Store[]>({
    queryKey: ['storesForBusiness', businessId],
    queryFn: async () => {
      if (!businessId) {
        return [];
      }

      const { data } = (await getAllStoresForBusiness(
        businessId,
      )) as AxiosResponse<GetStoresForBusinessOutput>;

      const storeListData = data.stores.map((store) => ({
        name: store.name,
        id: store.id,
        address: store.address,
        city: store.city,
        state: store.state,
        zipCode: store.zipCode,
        phoneNumber: store.phoneNumber,
        theme: store.storeTheme,
      }));

      const sortedStoreList = orderBy(
        storeListData,
        [(store) => store.name.toLowerCase()],
        ['asc'],
      );

      return sortedStoreList;
    },
    onError: (error) => {
      Sentry.captureException(error);

      toast.error(error?.response?.data?.error ?? THEME_MESSAGES.somethingWrong, {
        duration: 4000,
      });
      return error;
    },
    enabled: enabled && !!businessId,
    initialData: [],
  });
};
